/**
 * Audit trail — an immutable, timestamped record. Corrections annotate model
 * output; they never rewrite it. That is what makes the escape-rate figure
 * defensible in front of a customer's quality auditor.
 *
 * Exports are REAL: CSV downloads a file; PDF opens the print dialog with a
 * print stylesheet that strips chrome and keeps the table.
 */
import { useState } from 'react';
import { api, qs } from '../api/client';
import { useApi, ago } from '../hooks/useApi';
import type { AuditEntry } from '../api/types';
import { Card, Empty, ErrorBox, Loading } from '../components/Metric';

function csvEscape(v: unknown): string {
  const s = typeof v === 'string' ? v : JSON.stringify(v) ?? '';
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function AuditPage() {
  const [limit, setLimit] = useState(100);
  const [filter, setFilter] = useState('');
  const audit = useApi<AuditEntry[]>(() => api.get<AuditEntry[]>(`/api/v1/audit${qs({ limit })}`), [limit]);

  if (audit.loading) return <Loading label="Loading audit trail…" />;
  if (audit.error) return <ErrorBox message={audit.error} />;

  const rows = (audit.data ?? []).filter((a) =>
    filter ? a.action.toLowerCase().includes(filter.toLowerCase()) : true,
  );
  const actions = [...new Set((audit.data ?? []).map((a) => a.action))].sort();

  function exportCsv() {
    const header = ['timestamp', 'action', 'entity', 'entity_id', 'actor_user_id', 'detail'];
    const lines = rows.map((a) =>
      [a.createdAt, a.action, a.entity ?? '', a.entityId ?? '', (a as { actorUserId?: string | null }).actorUserId ?? '', JSON.stringify(a.detail ?? {})]
        .map(csvEscape)
        .join(','),
    );
    const blob = new Blob([[header.join(','), ...lines].join('\n')], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `plasticvision-audit-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <>
      <div className="callout info no-print">
        <strong>Immutable and timestamped</strong>
        Every capture, override, baseline change and state transition is
        recorded with an actor and a timestamp. Model outputs are never
        overwritten by a human correction — the correction is stored alongside,
        so the original decision remains visible.
      </div>

      <div className="btn-row no-print" style={{ marginBottom: 14 }}>
        <select aria-label="Row count" style={{ maxWidth: 120 }} value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
          {[50, 100, 250, 500].map((n) => (
            <option key={n} value={n}>
              {n} rows
            </option>
          ))}
        </select>
        <select aria-label="Filter by action" style={{ maxWidth: 260 }} value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="">All actions</option>
          {actions.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
        <button className="btn sm" onClick={audit.reload}>
          Refresh
        </button>
        <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>
          {rows.length} of {audit.data?.length ?? 0} entries
        </span>
        <div style={{ flex: 1 }} />
        <button className="btn sm" onClick={exportCsv}>
          Export CSV
        </button>
        <button className="btn sm" onClick={() => window.print()}>
          Export PDF
        </button>
      </div>

      <Card title="Entries" className="no-print-none">
        {rows.length === 0 ? (
          <Empty title="No audit entries" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Actor</th>
                  <th>Action</th>
                  <th>Resource</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.id}>
                    <td style={{ whiteSpace: 'nowrap', fontSize: 12, color: 'var(--ink-3)' }}>
                      {new Date(a.createdAt).toLocaleString()}
                      <div style={{ fontSize: 11 }}>{ago(a.createdAt)}</div>
                    </td>
                    <td className="mono" style={{ fontSize: 11 }}>
                      {(a as { actorUserId?: string | null }).actorUserId
                        ? (a as { actorUserId: string }).actorUserId.slice(0, 8)
                        : 'system'}
                    </td>
                    <td>
                      <span className="mono" style={{ fontSize: 12 }}>{a.action}</span>
                    </td>
                    <td style={{ fontSize: 12.5, color: 'var(--ink-2)' }}>
                      {a.entity ?? '—'}
                      {a.entityId ? <span className="dim"> · {a.entityId.slice(0, 8)}</span> : null}
                    </td>
                    <td>
                      <details className="raw">
                        <summary>
                          {Object.entries(a.detail ?? {})
                            .slice(0, 3)
                            .map(([k, v]) => `${k}=${typeof v === 'object' ? '…' : String(v)}`)
                            .join('  ')}
                        </summary>
                        <pre>{JSON.stringify(a.detail, null, 2)}</pre>
                      </details>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
